import { getApi } from 'api';
import { AxiosResponse } from 'axios';
import { toNumber } from 'lodash';
import IUser from 'types/superadmin/ISuperAdmin';
import { shouldSendMfaOverride } from 'utils/mfa-override';

/**
 * Never form-encoded. `selectedCompanies` is appended index by index below; `mfaRequiredOverride`
 * has no place on these payloads at all - an ApiUser is a machine identity that the backend resolver
 * excludes above the override, so a value set here could never take effect, and FormData would
 * stringify an absent one into the literal "undefined" and 400 the save.
 */
const EXCLUDED_FORM_KEYS = ['selectedCompanies', 'mfaRequiredOverride'];

/**
 * Builds the JSON body for the SuperAdmin endpoints, carrying MfaRequiredOverride only when it is a
 * real boolean. The rest of the model is spread as-is, so without this a value read back into the
 * form would be echoed on every save - which 403s the whole save for a SuperAdmin that is
 * impersonating, and is a pointless write for everyone else.
 */
const toSuperAdminPayload = (superAdmin: IUser): Record<string, unknown> => {
  const { mfaRequiredOverride, ...rest } = superAdmin;
  const payload: Record<string, unknown> = { ...rest, languageId: toNumber(superAdmin.languageId) };

  if (shouldSendMfaOverride(mfaRequiredOverride)) {
    payload.mfaRequiredOverride = mfaRequiredOverride;
  }

  return payload;
};

// languageId is optional for api users: an unset value must be sent as an empty string so the
// backend binds it to null ("no language configured") instead of the literal string "undefined".
const normalizeFormValue = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

// SuperAdmin
export const getAllSuperAdminsRequest = async (): Promise<IUser[]> => {
  const response: AxiosResponse<IUser[]> = await getApi().get('User/GetAllSuperAdmins');
  return response.data;
};

export const updateSuperAdminRequest = async (superAdmin: IUser): Promise<void> => {
  await getApi().put(`User/SuperAdmin/${superAdmin.id}`, toSuperAdminPayload(superAdmin));
};

export const addSuperAdminRequest = async (superAdmin: IUser): Promise<number> => {
  const response: AxiosResponse<number> = await getApi().post(`User/SuperAdmin`, toSuperAdminPayload(superAdmin));
  return response.data;
};

export const deleteSuperAdminsRequest = async (superAdminIds: number[]): Promise<void> => {
  await getApi().delete(`User/SuperAdmin`, { data: { Ids: superAdminIds } });
};

// api users
export const getAllApiUsersRequest = async (): Promise<IUser[]> => {
  const response: AxiosResponse<IUser[]> = await getApi().get('User/GetAllApiUsers');
  return response.data;
};

export const updateApiUserRequest = async (user: IUser): Promise<void> => {
  const formData = new FormData();

  Object.keys(user).forEach(k => !EXCLUDED_FORM_KEYS.includes(k) && formData.append(k, user[k]));

  if (!user.password) {
    formData.delete('password');
  }

  Object.keys(user.selectedCompanies).forEach(key =>
    formData.append(`selectedCompanies[${key}]`, user.selectedCompanies[key]),
  );

  await getApi().put(`User/ApiUser/${user.id}`, formData, {
    headers: { 'Content-Type': `multipart/form-data` },
  });
};

export const addApiUserRequest = async (user: IUser): Promise<number> => {
  const formData = new FormData();

  Object.keys(user).forEach(k => !EXCLUDED_FORM_KEYS.includes(k) && formData.append(k, user[k]));
  Object.keys(user.selectedCompanies).forEach(key =>
    formData.append(`selectedCompanies[${key}]`, user.selectedCompanies[key]),
  );

  const response: AxiosResponse<number> = await getApi().post(`User/ApiUser`, formData, {
    headers: { 'Content-Type': `multipart/form-data` },
  });
  return response.data;
};

export const deleteApiUsersRequest = async (userIds: number[]): Promise<void> => {
  await getApi().delete(`User/ApiUser`, { data: { Ids: userIds } });
};

// account managers

export const getAllAccountManagers = async (): Promise<IUser[]> => {
  const response: AxiosResponse<IUser[]> = await getApi().get('User/GetAllAccountManagers');
  return response.data;
};

export const addAccountManagerRequest = async (user: IUser): Promise<number> => {
  const formData = new FormData();

  Object.keys(user).forEach(k => !EXCLUDED_FORM_KEYS.includes(k) && formData.append(k, user[k]));

  const response: AxiosResponse<number> = await getApi().post(`User/AccountManager`, formData, {
    headers: { 'Content-Type': `multipart/form-data` },
  });
  return response.data;
};
